/* eslint-disable prettier/prettier */
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../../../../styles/components/pageComponents/dashboard/admin/AdminProgramsOverview.module.css';
import { useCourseEnrollment } from '../../../../hooks/useCourseEnrollment';
import GenericTable from '../../../pageComponents/dashboard/coach/GenericTable'; // adjust path if needed
import ProgramForm from '../../../pageComponents/program/ProgramForm';

import {
	GraduationCap,
	Eye,
	Pencil,
	Users,
	Plus,
	ChevronLeft,
	ChevronRight,
} from 'lucide-react';
// Import your hook here
import { useCourseCatalog } from '../../../../hooks/useCourse';
import { useUserProfile } from '../../../../hooks/useUserProfile';
import { UserProfileCard } from '../../managerUsers/UserProfileCard';
import { useProgramsOverview } from '../../../../hooks/useProgramOverview';
import { Pagination } from '../../../controlVariations/Pagination';

export default function ProgramsOverview() {
	const navigate = useNavigate();

	const handleCreateClick = () => setIsCreating(true);
	// const handleCancelCreate = () => setIsCreating(false);
	const handleCancelCreate = () => {
		setIsCreating(false);

		fetchProgramsOverview({
			page: currentPage,
			limit: ITEMS_PER_PAGE,
		});
		// ✅ reload page
	};

	const [isCreating, setIsCreating] = useState(false);

	// Fetch data using your custom hook
	const ITEMS_PER_PAGE = 5;

	const {
		programs,
		total,
		loading: isLoading,
		error,
		fetchProgramsOverview,
	} = useProgramsOverview({
		page: 1,
		limit: ITEMS_PER_PAGE,
	});

	const { getCourseEnrollments } = useCourseEnrollment();

	const [selectedUser, setSelectedUser] = useState<any>(null);
	const [isLearnersModalOpen, setIsLearnersModalOpen] = useState(false);
	const [learnersData, setLearnersData] = useState<any[]>([]);
	const { fetchUserProfileById } = useUserProfile();

	const [userData, setUserData] = useState<any>(null);
	const [isProfileOpen, setIsProfileOpen] = useState(false);

	const handleViewUser = async (row: any) => {
		try {
			setSelectedUser(row);

			const apiResponse = await fetchUserProfileById(row.id);

			if (apiResponse) {
				const roleName = apiResponse.rolename;

				// ✅ role mapping
				const formattedRole =
					roleName?.toLowerCase() === 'coachee'
						? 'Learner'
						: roleName;

				setUserData({
					name: apiResponse.fullname,
					email: apiResponse.email,
					enrolledProgram: row.program,
					phone: apiResponse.phonenumber,
					timezone: apiResponse.preferences?.timezone,
					location: apiResponse.location,
					avatarUrl: apiResponse.profilepicture,
					country: apiResponse.location?.split(',')[1]?.trim() || '',
					platformRole: formattedRole, // ✅ use mapped value
					organization: apiResponse.organizationname,
					professionalRole: apiResponse.designation,
					areasOfInterest: apiResponse.areasofinterest || [],
					coachingExperience: apiResponse.coachingexperience || [],
					growthWithIOCM: apiResponse.growthparameters || [],
				});

				setIsProfileOpen(true);
			}
		} catch (error) {
			console.error('Error fetching profile:', error);
		}
	};
	const handleViewLearners = async (courseId: string) => {
		try {
			const response = await getCourseEnrollments(courseId);

			if (response?.success && response.data) {
				const formatted = response.data.map((item: any) => ({
					id: item.userid,
					enrollmentId: item.id,
					name: item.username,
					program: item.coursetitle,
					credentials: item.usermetadata?.credentials || 'N/A',
					role: item.role || 'Learner',
				}));

				setLearnersData(formatted);
				setIsLearnersModalOpen(true);
			}
		} catch (err) {
			console.error('Error fetching learners:', err);
		}
	};

	// Pagination State
	const [currentPage, setCurrentPage] = useState(1);
	// const ITEMS_PER_PAGE = 5;

	useEffect(() => {
		fetchProgramsOverview({
			page: currentPage,
			limit: ITEMS_PER_PAGE,
		});
	}, [currentPage, fetchProgramsOverview]);

	const totalPages = Math.ceil(total / ITEMS_PER_PAGE);
	const showPagination = total > ITEMS_PER_PAGE;

	const paginatedCourses = programs;

	// Handlers
	const handlePrevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
	const handleNextPage = () =>
		setCurrentPage(prev => Math.min(prev + 1, totalPages));
	const handlePageClick = (pageNumber: number) => setCurrentPage(pageNumber);

	// Optional: Loading and Error states

	if (error)
		return (
			<div className={styles.container}>
				Error loading programs: {error}
			</div>
		);

	return (
		<div className={styles.container}>
			{/* Header Section */}
			<header className={styles.header}>
				<div className={styles.titleGroup}>
					<div className={styles.iconWrapper}>
						<GraduationCap
							size={24}
							className={styles.headerIcon}
						/>
					</div>
					<h1 className={styles.title}>PROGRAMS OVERVIEW</h1>
				</div>
				<button
					className={styles.createButton}
					onClick={handleCreateClick}
				>
					Create Program <Plus size={18} />
				</button>
			</header>

			{/* Table Section */}
			<div className={styles.tableCard}>
				<div className={styles.tableResponsiveWrapper}>
					<table className={styles.table}>
						<thead>
							<tr>
								<th className={styles.thLeft}>Program Name</th>
								<th className={styles.thCenter}>Coaches</th>
								<th className={styles.thCenter}>Learners</th>
								<th className={styles.thCenter}>Resources</th>
								<th className={styles.thCenter}>
									Masterclasses
								</th>
								<th className={styles.thCenter}>Sessions</th>
								<th className={styles.thLeft}>
									Completion Rate
								</th>
								<th className={styles.thLeft}>Quick Actions</th>
							</tr>
						</thead>
						<tbody>
							{paginatedCourses.map((course: any, index) => {
								const sessionCompletionRate =
									course.sessions?.total > 0
										? (course.sessions.done / course.sessions.total) * 100
										: 0;

								return (
									<tr
										key={course.id || index}
										className={styles.tableRow}
									>
										{/* Adjust these properties based on your actual CourseSummary type */}
										<td className={styles.tdLeft}>
											{course.title || 'Untitled Program'}
										</td>
										<td className={styles.tdCenter}>
											{course.coachcount ?? 0}
										</td>
										<td className={styles.tdCenter}>
											{course.learnercount || 0}
										</td>
										<td className={styles.tdCenter}>
											{course.resourcecount || 0}
										</td>
										<td className={styles.tdCenter}>
											{course.masterclasses?.label || '0/0'}
										</td>
										<td className={styles.tdCenter}>
											{course.sessions?.label || '0/0'}
										</td>
										<td className={styles.tdLeft}>
											<div className={styles.progressBarBg}>
												<div
													className={
														styles.progressBarFill
													}
													style={{
														width: `${sessionCompletionRate || 0}%`,
													}}
												/>
											</div>
										</td>
										<td className={styles.tdLeft}>
											<div className={styles.actionsGroup}>
												{/* <button className={styles.actionBtn} aria-label="View"  onClick={() => navigate(`/dashboard/programs/${course.id}`)}>
                        <Eye size={16} />
                      </button> */}
												<div
													className={
														styles.tooltipWrapper
													}
												>
													<button
														className={styles.actionBtn}
														aria-label="View"
														onClick={() =>
															navigate(
																`/dashboard/programs/${course.id}`
															)
														}
													>
														<Eye size={16} />
													</button>
													<span
														className={styles.tooltip}
													>
														View Program
													</span>
												</div>
												{/* <div className={styles.tooltipWrapper}>
												<button className={styles.actionBtn} aria-label="Edit" onClick={() => navigate(`/dashboard/programs/${course.id}`)}>
													<Pencil size={16} />
												</button>
												<span className={styles.tooltip}>Edit Program</span>
											</div> */}
												<div
													className={
														styles.tooltipWrapper
													}
												>
													<button
														className={styles.actionBtn}
														aria-label="Manage Users"
														onClick={() =>
															handleViewLearners(
																course.id
															)
														}
													>
														<Users size={16} />
													</button>
													<span
														className={styles.tooltip}
													>
														View Users
													</span>
												</div>
											</div>
										</td>
									</tr>
								);
							})}
							{paginatedCourses.length === 0 && (
								<tr>
									<td
										colSpan={8}
										className={styles.tdCenter}
										style={{ padding: '24px' }}
									>
										No programs found.
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>

				{/* Footer Pagination (Conditionally Rendered) */}
				{showPagination && (
					<div className={styles.paginationFooter}>
						<Pagination
							currentPage={currentPage}
							totalPages={totalPages}
							onPageChange={setCurrentPage}
							totalItems={total}
							pageSize={ITEMS_PER_PAGE}
							summaryText={`Showing ${(currentPage - 1) * ITEMS_PER_PAGE + 1
								} to ${Math.min(
									currentPage * ITEMS_PER_PAGE,
									total
								)} of ${total} entries`}
						/>
					</div>
				)}
			</div>

			{isLearnersModalOpen && (
				<GenericTable
					title="Enrolled Users"
					columns={[
						{ key: 'name', label: 'Full Name' },
						{ key: 'program', label: 'Enrolled Program' },
						{ key: 'credentials', label: 'Credentials' },
						{ key: 'role', label: 'Role' },
						{ key: 'actions', label: 'Actions' },
					]}
					data={learnersData}
					onClose={() => setIsLearnersModalOpen(false)}
					onView={handleViewUser}
				/>
			)}

			{isProfileOpen && userData && (
				<div className={styles.modalOverlay}>
					<div className={styles.modalContent}>
						<UserProfileCard
							mode="view"
							title="View User Profile"
							data={userData}
							showClose
							onClose={() => setIsProfileOpen(false)}
							showEdit={false}
							showDelete={false}
						/>
					</div>
				</div>
			)}

			{isCreating && (
				<div className={styles.modalOverlay}>
					<div className={styles.modalContentLarge}>
						<ProgramForm
							onBack={handleCancelCreate}
							onSuccess={newCourse => {
								fetchProgramsOverview({
									page: 1,
									limit: ITEMS_PER_PAGE,
								});

								setCurrentPage(1);
								setTimeout(() => {
									setIsCreating(false);
									setCurrentPage(1);
								}, 2500); // match success modal duration
							}}
						/>
					</div>
				</div>
			)}
		</div>
	);
}
